import { z } from 'zod';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { ApiError } from './api';
// Reusamos los errores tipados del coach: el contrato M2.2 mantiene los mismos
// códigos para 503 (anthropic no configurado / presupuesto agotado), 502
// (upstream) y 422 (plan inválido) porque el endpoint POST /api/season/plan
// también invoca a Anthropic vía tool-use.
import {
  CoachBadRequestError,
  CoachBudgetExceededError,
  CoachInvalidPlanError,
  CoachNotConfiguredError,
  CoachUpstreamError,
} from './coach';

/**
 * Cliente HTTP + hooks para la API de temporada (M2.2 — macrociclo).
 *
 * Igual que `coach.ts` y `strava.ts`, NO usamos el helper `request` de `./api`:
 * el endpoint `POST /api/season/plan` invoca al coach IA y necesita el mapeo
 * de errores Anthropic específico (503 keys/presupuesto, 502 upstream, 422
 * plan inválido) para que la UI pueda guiar al usuario al ajuste correcto.
 *
 * Decisiones de diseño:
 * - Reusamos las clases de error del coach (`CoachNotConfiguredError`,
 *   `CoachBudgetExceededError`, `CoachUpstreamError`, `CoachInvalidPlanError`,
 *   `CoachBadRequestError`) porque el contrato es idéntico. Evita duplicar la
 *   jerarquía de errores y permite que la UI use el mismo `mapCoachError`.
 * - Query key `['season', 'active']` para el plan activo y `['season', 'list']`
 *   para el histórico. Las mutaciones invalidan ambas (un POST archiva el
 *   anterior y crea uno nuevo).
 */

const API_BASE_URL: string = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

// ---------------------------------------------------------------------------
// Re-exports (los errores son los mismos que en coach.ts)
// ---------------------------------------------------------------------------

export {
  CoachBadRequestError,
  CoachBudgetExceededError,
  CoachInvalidPlanError,
  CoachNotConfiguredError,
  CoachUpstreamError,
};

// ---------------------------------------------------------------------------
// Schemas
// ---------------------------------------------------------------------------

export const EVENT_TYPES = ['sprint', 'olympic', '70.3', 'ironman', 'other'] as const;
export type EventType = (typeof EVENT_TYPES)[number];

export const PHASE_TYPES = [
  'base',
  'build',
  'peak',
  'taper',
  'race_week',
  'recovery',
  'transition',
] as const;
export type PhaseType = (typeof PHASE_TYPES)[number];

export const SEASON_STATUSES = ['active', 'archived', 'completed'] as const;
export type SeasonStatus = (typeof SEASON_STATUSES)[number];

/**
 * Distribución de intensidad por zonas. Todas las claves son opcionales —
 * algunas fases (transition, recovery) pueden tener distribuciones más
 * simples.
 */
export const IntensityDistributionSchema = z
  .object({
    z1: z.number().min(0).max(100).optional(),
    z2: z.number().min(0).max(100).optional(),
    z3: z.number().min(0).max(100).optional(),
    z4: z.number().min(0).max(100).optional(),
    z5: z.number().min(0).max(100).optional(),
  })
  .partial();
export type IntensityDistribution = z.infer<typeof IntensityDistributionSchema>;

export const SeasonPhaseSchema = z.object({
  id: z.number().int().positive(),
  seasonPlanId: z.number().int().positive(),
  idx: z.number().int().nonnegative(),
  name: z.string().min(1),
  phaseType: z.enum(PHASE_TYPES),
  startDateISO: z.string().min(1),
  endDateISO: z.string().min(1),
  targetWeeklyHours: z.number().nonnegative(),
  focus: z.string().nullable(),
  intensityDistribution: IntensityDistributionSchema.nullable(),
});
export type SeasonPhase = z.infer<typeof SeasonPhaseSchema>;

export const SeasonPlanSchema = z.object({
  id: z.number().int().positive(),
  profileId: z.number().int().positive(),
  name: z.string().min(1),
  eventType: z.enum(EVENT_TYPES),
  eventDateISO: z.string().min(1),
  goalDescription: z.string().nullable(),
  weeklyHoursBaseline: z.number().int().positive(),
  status: z.enum(SEASON_STATUSES),
  createdAt: z.string(),
  updatedAt: z.string(),
  phases: z.array(SeasonPhaseSchema),
});
export type SeasonPlan = z.infer<typeof SeasonPlanSchema>;

export const CreateSeasonPlanBodySchema = z.object({
  name: z.string().min(1, 'El nombre es obligatorio').max(200),
  eventType: z.enum(EVENT_TYPES),
  eventDateISO: z.string().min(1, 'La fecha del evento es obligatoria'),
  goalDescription: z.string().max(2000).optional(),
  weeklyHoursBaseline: z.number().int().min(1).max(30),
});
export type CreateSeasonPlanBody = z.infer<typeof CreateSeasonPlanBodySchema>;

export const CreateSeasonPlanResponseSchema = z.object({
  plan: SeasonPlanSchema,
  tokensUsed: z.number().int().nonnegative(),
  latencyMs: z.number().nonnegative(),
});
export type CreateSeasonPlanResponse = z.infer<typeof CreateSeasonPlanResponseSchema>;

export const SetSeasonStatusBodySchema = z.object({
  status: z.enum(SEASON_STATUSES),
});
export type SetSeasonStatusBody = z.infer<typeof SetSeasonStatusBodySchema>;

// GET /api/season/active devuelve `{ plan: SeasonPlan | null }`. El backend
// envuelve siempre en `plan` para mantener forward-compat (podrían añadirse
// metadatos al objeto raíz sin cambiar el contrato).
const ActiveSeasonResponseSchema = z.object({
  plan: SeasonPlanSchema.nullable(),
});
// GET /api/season/list devuelve `{ plans: SeasonPlan[] }`.
const SeasonListResponseSchema = z.object({
  plans: z.array(SeasonPlanSchema),
});

// ---------------------------------------------------------------------------
// HTTP helpers (mismos patrones que coach.ts)
// ---------------------------------------------------------------------------

async function readJsonSafe(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function pickString(body: unknown, key: string): string | undefined {
  if (body && typeof body === 'object' && key in body) {
    const v = (body as Record<string, unknown>)[key];
    if (typeof v === 'string') return v;
  }
  return undefined;
}

function pickIssues(body: unknown): string[] {
  if (!body || typeof body !== 'object') return [];
  const issues = (body as Record<string, unknown>).issues;
  if (!Array.isArray(issues)) return [];
  const out: string[] = [];
  for (const issue of issues) {
    if (typeof issue === 'string') {
      out.push(issue);
    } else if (issue && typeof issue === 'object') {
      const msg = (issue as Record<string, unknown>).message;
      if (typeof msg === 'string') out.push(msg);
    }
  }
  return out;
}

/**
 * Mapea respuestas no-OK del endpoint `POST /api/season/plan` (que invoca a
 * Anthropic) a las clases de error tipadas. Mismo contrato que `/api/coach/plan`.
 */
function throwCoachLikeError(response: Response, json: unknown): never {
  const errStr = pickString(json, 'error') ?? '';
  const detail = pickString(json, 'detail') ?? pickString(json, 'message') ?? '';
  const hint = pickString(json, 'hint') ?? '';

  if (response.status === 503) {
    if (errStr === 'anthropic_budget_exceeded') {
      throw new CoachBudgetExceededError(hint);
    }
    throw new CoachNotConfiguredError(hint);
  }
  if (response.status === 502 || errStr === 'anthropic_error') {
    throw new CoachUpstreamError();
  }
  if (response.status === 422) {
    throw new CoachInvalidPlanError(detail);
  }
  if (response.status === 400) {
    throw new CoachBadRequestError(pickIssues(json), detail);
  }

  throw new ApiError(`Respuesta no OK del backend: ${response.status}`, response.status);
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

/** GET /api/season/active → { plan: SeasonPlan | null } → unwrap a SeasonPlan | null */
export async function getActiveSeason(): Promise<SeasonPlan | null> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/api/season/active`, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }
  if (response.status !== 200) {
    throw new ApiError(`Respuesta no OK del backend: ${response.status}`, response.status);
  }
  const json = await readJsonSafe(response);
  const parsed = ActiveSeasonResponseSchema.safeParse(json);
  if (!parsed.success) {
    throw new ApiError('Respuesta /api/season/active no cumple esquema', 200, parsed.error);
  }
  return parsed.data.plan;
}

/** GET /api/season/list → { plans: SeasonPlan[] } → unwrap a SeasonPlan[] */
export async function listSeasons(): Promise<SeasonPlan[]> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/api/season/list`, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }
  if (response.status !== 200) {
    throw new ApiError(`Respuesta no OK del backend: ${response.status}`, response.status);
  }
  const json = await readJsonSafe(response);
  const parsed = SeasonListResponseSchema.safeParse(json);
  if (!parsed.success) {
    throw new ApiError('Respuesta /api/season/list no cumple esquema', 200, parsed.error);
  }
  return parsed.data.plans;
}

/**
 * POST /api/season/plan
 *
 * Latencia esperada: 10-30s. Si ya existe un plan activo, el backend lo
 * archiva en la misma transacción y crea el nuevo.
 */
export async function createSeasonPlan(
  body: CreateSeasonPlanBody,
): Promise<CreateSeasonPlanResponse> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/api/season/plan`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }

  if (response.status === 200 || response.status === 201) {
    const json = await readJsonSafe(response);
    const parsed = CreateSeasonPlanResponseSchema.safeParse(json);
    if (!parsed.success) {
      throw new ApiError('Respuesta /api/season/plan no cumple esquema', response.status, parsed.error);
    }
    return parsed.data;
  }

  const json = await readJsonSafe(response);
  throwCoachLikeError(response, json);
}

/** PATCH /api/season/:id/status → backend devuelve `{ plan: SeasonPlan | null }`. */
const PatchSeasonStatusResponseSchema = z.object({
  plan: SeasonPlanSchema.nullable(),
});

export async function setSeasonStatus(
  id: number,
  status: SeasonStatus,
): Promise<SeasonPlan> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/api/season/${id}/status`, {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ status }),
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }
  if (response.status !== 200) {
    throw new ApiError(`Respuesta no OK del backend: ${response.status}`, response.status);
  }
  const json = await readJsonSafe(response);
  const parsed = PatchSeasonStatusResponseSchema.safeParse(json);
  if (!parsed.success) {
    throw new ApiError(`Respuesta /api/season/${id}/status no cumple esquema`, 200, parsed.error);
  }
  if (parsed.data.plan === null) {
    throw new ApiError(`/api/season/${id}/status devolvió plan=null tras cambio de estado`, 200);
  }
  return parsed.data.plan;
}

/** DELETE /api/season/:id → 204 */
export async function deleteSeason(id: number): Promise<void> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/api/season/${id}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }
  if (response.status !== 204 && response.status !== 200) {
    throw new ApiError(`Respuesta no OK del backend: ${response.status}`, response.status);
  }
}

// ---------------------------------------------------------------------------
// Hooks (TanStack Query)
// ---------------------------------------------------------------------------

const SEASON_ACTIVE_KEY = ['season', 'active'] as const;
const SEASON_LIST_KEY = ['season', 'list'] as const;

export function useActiveSeason(): UseQueryResult<SeasonPlan | null, Error> {
  return useQuery<SeasonPlan | null, Error>({
    queryKey: SEASON_ACTIVE_KEY,
    queryFn: () => getActiveSeason(),
    // No reintentamos automáticamente porque cualquier error aquí es
    // probablemente 401 (sesión caducada) o 5xx del backend; mejor que la UI
    // lo gestione explícitamente.
    retry: false,
  });
}

export function useSeasonList(): UseQueryResult<SeasonPlan[], Error> {
  return useQuery<SeasonPlan[], Error>({
    queryKey: SEASON_LIST_KEY,
    queryFn: () => listSeasons(),
    retry: false,
  });
}

/**
 * `useCreateSeasonPlan` — mutation.
 *
 * Invalida `['season', 'active']` y `['season', 'list']` al éxito. El backend
 * archiva el plan activo anterior en la misma transacción, así que ambos
 * caches quedan desactualizados.
 */
export function useCreateSeasonPlan(): UseMutationResult<
  CreateSeasonPlanResponse,
  Error,
  CreateSeasonPlanBody
> {
  const qc = useQueryClient();
  return useMutation<CreateSeasonPlanResponse, Error, CreateSeasonPlanBody>({
    mutationFn: (body) => createSeasonPlan(body),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: SEASON_ACTIVE_KEY }),
        qc.invalidateQueries({ queryKey: SEASON_LIST_KEY }),
      ]);
    },
  });
}

export function useSetSeasonStatus(): UseMutationResult<
  SeasonPlan,
  Error,
  { id: number; status: SeasonStatus }
> {
  const qc = useQueryClient();
  return useMutation<SeasonPlan, Error, { id: number; status: SeasonStatus }>({
    mutationFn: ({ id, status }) => setSeasonStatus(id, status),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: SEASON_ACTIVE_KEY }),
        qc.invalidateQueries({ queryKey: SEASON_LIST_KEY }),
      ]);
    },
  });
}

export function useDeleteSeason(): UseMutationResult<void, Error, number> {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (id) => deleteSeason(id),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: SEASON_ACTIVE_KEY }),
        qc.invalidateQueries({ queryKey: SEASON_LIST_KEY }),
      ]);
    },
  });
}

// ---------------------------------------------------------------------------
// Helpers de display (puros — reutilizables por la UI y tests)
// ---------------------------------------------------------------------------

export function eventTypeLabel(t: EventType): string {
  switch (t) {
    case 'sprint': return 'Sprint';
    case 'olympic': return 'Olímpico';
    case '70.3': return 'Medio Ironman (70.3)';
    case 'ironman': return 'Ironman';
    case 'other': return 'Otro';
  }
}

export function phaseTypeLabel(t: PhaseType): string {
  switch (t) {
    case 'base': return 'Base';
    case 'build': return 'Build';
    case 'peak': return 'Peak';
    case 'taper': return 'Taper';
    case 'race_week': return 'Race week';
    case 'recovery': return 'Recovery';
    case 'transition': return 'Transición';
  }
}

export function statusLabel(s: SeasonStatus): string {
  switch (s) {
    case 'active': return 'Activo';
    case 'archived': return 'Archivado';
    case 'completed': return 'Completado';
  }
}
